import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Users, Car, Percent, RotateCcw } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import SuperAdminOnlyNote from "../components/SuperAdminOnlyNote";
import api from "../services/api";
import { isSuperAdmin } from "../utils/role";

// Every money value on the platform is sourced from the backend (the "bucket").
// This page reads and writes those values so an admin change reflects instantly
// in the mobile app. No prices are hardcoded here.

const FIELDS = [
  {
    key: "normalFarePerSeatNgn",
    label: "Normal ride fare",
    unit: "per seat",
    icon: Users,
    help: "Charged per seat on a shared keke ride.",
  },
  {
    key: "charterFareNgn",
    label: "Charter fare",
    unit: "flat / whole keke",
    icon: Car,
    help: "Flat price to book the entire keke (private ride).",
  },
  {
    key: "commissionPerSeatNgn",
    label: "Platform charge",
    unit: "per seat",
    icon: Percent,
    help: "Added on top of the fare; this is Zooma's commission per seat.",
  },
];

const naira = (n) =>
  "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });

const Pricing = () => {
  const [pricing, setPricing] = useState(null); // values from the backend
  const [form, setForm] = useState({}); // editable copy
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canEdit = isSuperAdmin();

  const loadPricing = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/admin/pricing");
      const values = {
        normalFarePerSeatNgn: data.normalFarePerSeatNgn,
        charterFareNgn: data.charterFareNgn,
        commissionPerSeatNgn: data.commissionPerSeatNgn,
      };
      setPricing(values);
      setForm(values);
    } catch (error) {
      console.error("Failed to load pricing:", error);
      toast.error(
        error.response?.data?.message || "Failed to load pricing settings"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, []);

  const handleChange = (key, value) => {
    // keep it numeric-only; allow empty string while editing
    const cleaned = value.replace(/[^\d]/g, "");
    setForm((prev) => ({ ...prev, [key]: cleaned === "" ? "" : Number(cleaned) }));
  };

  const isDirty =
    pricing &&
    FIELDS.some((f) => String(form[f.key]) !== String(pricing[f.key]));

  const isValid = FIELDS.every(
    (f) => form[f.key] !== "" && Number(form[f.key]) >= 0
  );

  const handleReset = () => setForm(pricing);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    if (!isValid) {
      toast.error("Please enter a valid amount for every field.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/v1/admin/pricing", {
        normalFarePerSeatNgn: Number(form.normalFarePerSeatNgn),
        charterFareNgn: Number(form.charterFareNgn),
        commissionPerSeatNgn: Number(form.commissionPerSeatNgn),
      });
      const values = {
        normalFarePerSeatNgn: data.normalFarePerSeatNgn,
        charterFareNgn: data.charterFareNgn,
        commissionPerSeatNgn: data.commissionPerSeatNgn,
      };
      setPricing(values);
      setForm(values);
      toast.success("Pricing updated — it now applies across the app.");
    } catch (error) {
      console.error("Failed to update pricing:", error);
      toast.error(
        error.response?.data?.message || "Failed to update pricing"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Pricing
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Set the fares and platform charge. Changes are saved to the
              backend and apply instantly across the app.
            </p>
            {!canEdit && <SuperAdminOnlyNote className="mt-3" />}
          </div>

          <form
            onSubmit={handleSave}
            className="max-w-2xl bg-white dark:bg-black rounded-xl shadow-sm"
          >
            {loading ? (
              <div className="p-10 flex items-center justify-center text-gray-500">
                <div className="h-6 w-6 border-2 border-gray-300 border-t-[#FF8C00] rounded-full animate-spin mr-3" />
                Loading pricing…
              </div>
            ) : (
              <>
                {FIELDS.map((field, i) => {
                  const Icon = field.icon;
                  return (
                    <div
                      key={field.key}
                      className={`flex items-start justify-between gap-4 py-5 px-6 ${
                        i !== 0 ? "border-t border-gray-100 dark:border-gray-800" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-[#FFF5E6] text-[#FF8C00] flex items-center justify-center">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {field.label}{" "}
                            <span className="text-xs font-normal text-gray-400">
                              ({field.unit})
                            </span>
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {field.help}
                          </p>
                        </div>
                      </div>

                      <div className="relative w-36 shrink-0">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                          ₦
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={form[field.key] ?? ""}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          readOnly={!canEdit}
                          className="w-full read-only:bg-gray-50 read-only:text-gray-500 read-only:focus:ring-0 border border-gray-200 dark:border-gray-700 rounded-lg pl-7 pr-3 py-2 text-sm text-right dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  );
                })}

                {/* Live example so the admin sees the effect before saving.
                    The customer pays fare + charge. */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-300 rounded-b-none">
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    Example (what the customer pays):
                  </span>{" "}
                  a 2-seat normal ride ={" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {naira(
                      ((form.normalFarePerSeatNgn || 0) +
                        (form.commissionPerSeatNgn || 0)) *
                        2
                    )}
                  </span>{" "}
                  ({naira((form.normalFarePerSeatNgn || 0) * 2)} fare +{" "}
                  {naira((form.commissionPerSeatNgn || 0) * 2)} charge); a charter ={" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {naira(
                      (form.charterFareNgn || 0) + (form.commissionPerSeatNgn || 0)
                    )}
                  </span>{" "}
                  ({naira(form.charterFareNgn)} fare +{" "}
                  {naira(form.commissionPerSeatNgn)} charge).
                </div>

                {canEdit && (
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-800">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={!isDirty || saving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={!isDirty || !isValid || saving}
                    className="px-5 py-2 bg-[#FF8C00] text-white text-sm font-medium rounded-lg hover:bg-[#e67e00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
                )}
              </>
            )}
          </form>
        </main>
      </div>
    </div>
  );
};

export default Pricing;
